import React, { useEffect, useMemo } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import {
  PRODUCTION_REPORT_ROUTES,
  getProductionPathForReport,
  getProductionReportFromPath,
} from '../../config/productionRoutes';
import TopNav from '../TopNav/TopNav';
import OnTimeDelivery from '../UserPage/OnTimeDelivery';

const ProductionReportsContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canView, isLoading: isPermissionsLoading } = usePermissions();
  const visibleReports = useMemo(
    () => PRODUCTION_REPORT_ROUTES.filter((report) => canView(report.permissionKey)),
    [canView]
  );
  const requestedReport = getProductionReportFromPath(location.pathname);
  const activeReport = requestedReport &&
    visibleReports.some((report) => report.id === requestedReport)
    ? requestedReport
    : visibleReports[0]?.id || '';

  useEffect(() => {
    if (isPermissionsLoading || visibleReports.length === 0 || !activeReport) {
      return;
    }

    const canonicalPath = getProductionPathForReport(activeReport);
    const currentPath = location.pathname.replace(/\/+$/, '').toLowerCase();
    if (canonicalPath && currentPath !== canonicalPath.toLowerCase()) {
      navigate(canonicalPath, { replace: true });
    }
  }, [
    activeReport,
    isPermissionsLoading,
    location.pathname,
    navigate,
    visibleReports.length,
  ]);

  const renderReport = () => {
    switch (activeReport) {
      case 'onTimeDelivery':
        return <OnTimeDelivery />;
      default:
        return null;
    }
  };

  return (
    <div>
      <TopNav />
      <main className="container-fluid p-3">
        <div className="mb-3">
          <h1 className="h2 mb-1">Production Reports</h1>
          <p className="text-muted mb-0">
            Review production performance and delivery reporting.
          </p>
        </div>

        {isPermissionsLoading ? (
          <div className="text-center p-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading report permissions...</span>
            </Spinner>
            <p className="mt-2">Loading report permissions...</p>
          </div>
        ) : visibleReports.length === 0 ? (
          <Alert variant="warning">
            You do not currently have access to any production reports.
          </Alert>
        ) : (
          <>
            <ul className="nav nav-tabs mb-4" aria-label="Production reports">
              {visibleReports.map((report) => (
                <li className="nav-item" key={report.id}>
                  <button
                    type="button"
                    className={`nav-link ${activeReport === report.id ? 'active' : ''}`}
                    onClick={() => navigate(getProductionPathForReport(report.id))}
                  >
                    {report.label}
                  </button>
                </li>
              ))}
            </ul>
            <div className="tab-content">{renderReport()}</div>
          </>
        )}
      </main>
    </div>
  );
};

export default ProductionReportsContainer;
